/**
 * @fileoverview Composable for storage adapter operations with Result pattern
 * @description Provides standardized CRUD operations with optimistic locking and error handling
 *
 * Features:
 * - Result pattern for error handling
 * - Version tracking for optimistic locking
 * - Conflict detection and event emission
 * - Auth error handling
 * - Optimistic updates with rollback
 */

import { ref, type Ref } from 'vue'
import type {
  IStorageAdapter,
  Result,
  StoredData,
  SaveOptions,
  ConflictInfo,
} from '@/services/storage/IStorageAdapter'
import { StorageErrorCode } from '@/services/storage/IStorageAdapter'

/**
 * Error state for UI display
 */
export interface StorageErrorState {
  code: StorageErrorCode
  message: string
}

/**
 * Result type for store operations
 */
export interface StoreOperationResult<T = void> {
  success: boolean
  error?: StorageErrorState
  conflict?: ConflictInfo<T>
}

/**
 * Event handlers
 */
type ConflictHandler<T> = (_conflict: ConflictInfo<T>) => void
type AuthErrorHandler = () => void

/**
 * Composable for storage adapter operations
 */
export function useStorageAdapter<T>() {
  const adapter: Ref<IStorageAdapter<T> | null> = ref(null)
  const versions = ref<Map<string, number>>(new Map())
  const lastError: Ref<StorageErrorState | null> = ref(null)
  const conflictHandlers: ConflictHandler<T>[] = []
  const authErrorHandlers: AuthErrorHandler[] = []

  /**
   * Set storage adapter
   */
  function setAdapter(newAdapter: IStorageAdapter<T>): void {
    adapter.value = newAdapter
  }

  /**
   * Get adapter (throw if not set)
   */
  function getAdapter(): IStorageAdapter<T> | null {
    return adapter.value
  }

  /**
   * Register conflict handler
   */
  function onConflict(handler: ConflictHandler<T>): () => void {
    conflictHandlers.push(handler)
    // Return unsubscribe function
    return () => {
      const index = conflictHandlers.indexOf(handler)
      if (index > -1) {
        conflictHandlers.splice(index, 1)
      }
    }
  }

  /**
   * Register auth error handler
   */
  function onAuthError(handler: AuthErrorHandler): () => void {
    authErrorHandlers.push(handler)
    // Return unsubscribe function
    return () => {
      const index = authErrorHandlers.indexOf(handler)
      if (index > -1) {
        authErrorHandlers.splice(index, 1)
      }
    }
  }

  /**
   * Emit conflict event
   */
  function emitConflict(conflict: ConflictInfo<T>): void {
    conflictHandlers.forEach(handler => handler(conflict))
  }

  /**
   * Emit auth error event
   */
  function emitAuthError(): void {
    authErrorHandlers.forEach(handler => handler())
  }

  /**
   * Load data from storage
   */
  async function load(key: string): Promise<Result<StoredData<T> | null>> {
    if (!adapter.value) {
      return { success: false, error: new Error('Storage adapter not initialized') }
    }

    lastError.value = null

    const result = await adapter.value.get(key)

    if (!result.success) {
      const error = result.error as { code?: StorageErrorCode; message: string }
      lastError.value = {
        code: error.code || StorageErrorCode.UNKNOWN,
        message: error.message,
      }

      // Emit auth error if applicable
      if (error.code === StorageErrorCode.AUTH_ERROR) {
        emitAuthError()
      }

      return result
    }

    // Track version if data exists
    if (result.data) {
      versions.value.set(key, result.data.metadata.version)
    }

    return result
  }

  /**
   * Save data to storage with optimistic locking
   *
   * This function automatically handles version tracking:
   * - If no version is tracked for this key, it will first load the current data
   *   to get the version (if it exists) before saving
   * - This prevents 400 Bad Request errors when trying to PUT without a version
   * - After successful save, the new version is automatically tracked
   */
  async function save(
    key: string,
    data: T,
    options?: SaveOptions
  ): Promise<StoreOperationResult<T>> {
    if (!adapter.value) {
      return {
        success: false,
        error: {
          code: StorageErrorCode.UNKNOWN,
          message: 'Storage adapter not initialized',
        },
      }
    }

    lastError.value = null

    // If no version is tracked and none was provided, try to load existing data first
    // This ensures we have the correct version for optimistic locking on updates
    let expectedVersion = options?.expectedVersion ?? versions.value.get(key)

    if (expectedVersion === undefined) {
      // Try to load existing data to get the current version
      const existingResult = await adapter.value.get(key)
      if (existingResult.success && existingResult.data) {
        expectedVersion = existingResult.data.metadata.version
        versions.value.set(key, expectedVersion)
      }
      // If no existing data, expectedVersion remains undefined (will use POST for create)
    }

    const saveOptions: SaveOptions =
      expectedVersion !== undefined ? { ...options, expectedVersion } : { ...options }

    const result = await adapter.value.save(key, data, saveOptions)

    if (!result.success) {
      // Check if it's a conflict
      const error = result.error as ConflictInfo<T> | { code?: StorageErrorCode; message: string }

      if ('currentVersion' in error && 'attemptedVersion' in error) {
        // It's a conflict
        const conflict = error as ConflictInfo<T>
        emitConflict(conflict)

        return {
          success: false,
          conflict,
          error: {
            code: StorageErrorCode.CONFLICT,
            message: 'Version conflict detected',
          },
        }
      }

      // It's a regular error
      const storageError = error as { code?: StorageErrorCode; message: string }
      lastError.value = {
        code: storageError.code || StorageErrorCode.UNKNOWN,
        message: storageError.message,
      }

      // Emit auth error if applicable
      if (storageError.code === StorageErrorCode.AUTH_ERROR) {
        emitAuthError()
      }

      return {
        success: false,
        error: lastError.value,
      }
    }

    // Update tracked version on success
    versions.value.set(key, result.data.metadata.version)

    return { success: true }
  }

  /**
   * Get tracked version for a key
   */
  function getVersion(key: string): number | undefined {
    return versions.value.get(key)
  }

  /**
   * Clear tracked version
   */
  function clearVersion(key: string): void {
    versions.value.delete(key)
  }

  /**
   * Ensure version is tracked for a key by loading from storage if needed.
   * This is useful to call proactively before a series of operations
   * to avoid the overhead of loading during save.
   *
   * @returns The tracked version, or undefined if no data exists
   */
  async function ensureVersionTracked(key: string): Promise<number | undefined> {
    if (!adapter.value) {
      return undefined
    }

    // If already tracked, return it
    const existing = versions.value.get(key)
    if (existing !== undefined) {
      return existing
    }

    // Load from storage to get and track the version
    const result = await adapter.value.get(key)
    if (result.success && result.data) {
      versions.value.set(key, result.data.metadata.version)
      return result.data.metadata.version
    }

    return undefined
  }

  return {
    adapter: getAdapter,
    setAdapter,
    load,
    save,
    getVersion,
    clearVersion,
    ensureVersionTracked,
    lastError,
    onConflict,
    onAuthError,
  }
}
